// client/src/hooks/useArquivos.js - VERSÃO 100% CORRIGIDA E COMPLETA
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import useAlert from './useAlert';

const ARQUIVOS_QUERY_KEY = 'arquivos';

// ✅ Configuração robusta da API
const apiClient = axios.create({
    baseURL: process.env.REACT_APP_API_URL || 'http://localhost:50000/api/v1',
    headers: {
        'Accept': 'application/json',
        // ✅ SEGURANÇA: ver mesmo header em services/api.js — identifica
        // a chamada como vinda do painel web pro backend bloquear scope
        // 'conta' aqui (exclusivo pra matriz/franquia).
        'X-Client-Platform': 'web',
    },
    timeout: 60000,
});

// ✅ Interceptor de requisição
apiClient.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('token') || sessionStorage.getItem('token');
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        
        if (config.url?.includes('/upload') || config.url?.includes('/galeria') || config.url?.includes('/arquivos')) {
            console.log(`🌐 [API REQUEST ${config.method?.toUpperCase()}] ${config.url}`, {
                params: config.params,
                hasFile: config.data instanceof FormData,
            });
        }
        
        return config;
    },
    (error) => Promise.reject(error)
);

// ✅ Interceptor de resposta
apiClient.interceptors.response.use(
    (response) => {
        if (response.config.url?.includes('/upload') || response.config.url?.includes('/arquivos')) {
            console.log(`✅ [API RESPONSE ${response.status}] ${response.config.url}`, {
                success: response.data?.success,
                count: response.data?.count,
                dataLength: Array.isArray(response.data?.data) ? response.data.data.length : 'N/A'
            });
        }
        return response;
    },
    (error) => {
        console.error(`❌ [API ERROR] ${error.config?.method?.toUpperCase()} ${error.config?.url}`, {
            status: error.response?.status,
            statusText: error.response?.statusText,
            data: error.response?.data,
            message: error.message,
            params: error.config?.params
        });
        return Promise.reject(error);
    }
);

/**
 * ✅ Hook principal para buscar arquivos - VERSÃO CORRIGIDA
 * Usa apenas a rota /arquivos conforme controller
 */
export const useArquivos = (params = {}) => {
    const { referenciaId, model, categoria } = params;
    
    return useQuery({
        queryKey: [ARQUIVOS_QUERY_KEY, params],
        queryFn: async () => {
            console.log('📁 [useArquivos] Buscando arquivos com parâmetros:', params);
            
            if (!referenciaId || !model) {
                console.warn('⚠️ [useArquivos] Parâmetros obrigatórios faltando');
                return [];
            }
            
            try {
                // ✅ USAR APENAS A ROTA DE ARQUIVOS (conforme controller)
                const response = await apiClient.get('/arquivos', {
                    params: { 
                        referenciaId, 
                        model: model.toUpperCase(), // ✅ GARANTIR UPPERCASE
                        ...(categoria && { categoria: categoria.toUpperCase() }) // ✅ GARANTIR UPPERCASE
                    },
                    headers: { 
                        'Cache-Control': 'no-cache',
                        'Pragma': 'no-cache'
                    }
                });
                
                console.log('📡 [useArquivos] Resposta completa da API:', {
                    status: response.status,
                    success: response.data?.success,
                    count: response.data?.count,
                    hasData: !!response.data?.data,
                    isArray: Array.isArray(response.data?.data),
                    data: response.data?.data
                });
                
                // ✅ CORREÇÃO: Verificar estrutura da resposta
                if (response.data?.success) {
                    let arquivos = [];
                    
                    if (Array.isArray(response.data.data)) {
                        arquivos = response.data.data;
                    } else if (response.data.data && typeof response.data.data === 'object') {
                        // Se data é um objeto único, colocar em array
                        arquivos = [response.data.data];
                    } else if (Array.isArray(response.data)) {
                        // Fallback: se a resposta já for um array
                        arquivos = response.data;
                    }
                    
                    console.log(`✅ [useArquivos] ${arquivos.length} arquivos processados`);
                    
                    // ✅ CORREÇÃO: Garantir que cada arquivo tenha URL
                    arquivos = arquivos.map(arquivo => ({
                        ...arquivo,
                        // Usar url do arquivo se existir, senão usar campo arquivo
                        url: arquivo.url || arquivo.arquivo,
                        // Garantir que temos os campos necessários
                        _id: arquivo._id || arquivo.id,
                        categoria: arquivo.categoria || 'GERAL',
                        model: arquivo.model,
                        referenciaId: arquivo.referenciaId
                    }));
                    
                    return arquivos;
                }
                
                console.warn('⚠️ [useArquivos] Resposta sem success:true', response.data);
                return [];
                
            } catch (error) {
                console.error('❌ [useArquivos] Erro na busca de arquivos:', {
                    message: error.message,
                    status: error.response?.status,
                    data: error.response?.data,
                    params: { referenciaId, model, categoria }
                });
                
                // Não lançar erro, retornar array vazio para UI não quebrar
                return [];
            }
        },
        enabled: !!referenciaId && !!model,
        staleTime: 1000 * 30, // 30 segundos
        gcTime: 1000 * 60 * 5, // 5 minutos
        refetchOnWindowFocus: false,
        retry: 2,
        retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 10000)
    });
};

/**
 * ✅ Hook para mutações de arquivos (upload/delete) - VERSÃO CORRIGIDA
 */
export const useMutateArquivos = () => {
    const queryClient = useQueryClient();
    const { setAlert } = useAlert();

    // ✅ Função para invalidar cache de forma inteligente
    const invalidateCache = (params) => {
        console.log('🔄 [useMutateArquivos] Invalidando cache para:', params);
        
        // Invalidar query específica
        if (params) {
            queryClient.invalidateQueries({ 
                queryKey: [ARQUIVOS_QUERY_KEY, params] 
            });
        }
        
        // Invalidar todas as queries de arquivos
        queryClient.invalidateQueries({ 
            queryKey: [ARQUIVOS_QUERY_KEY] 
        });
    };

    // ✅ Função para preparar FormData corretamente
    const prepareFormData = (formDataOrObject) => {
        let formData;
        
        if (formDataOrObject instanceof FormData) {
            formData = formDataOrObject;
        } else {
            formData = new FormData();
            const { file, referenciaId, model, categoria, descricao } = formDataOrObject;
            
            if (file) formData.append('file', file);
            if (referenciaId) formData.append('referenciaId', referenciaId);
            if (model) formData.append('model', model);
            if (categoria) formData.append('categoria', categoria);
            if (descricao) formData.append('descricao', descricao);
        }
        
        // DEBUG: Log do conteúdo do FormData
        console.log('📦 [FormData Contents]');
        for (let pair of formData.entries()) {
            const key = pair[0];
            const value = key === 'file' 
                ? `[File: ${pair[1].name}, ${pair[1].type}, ${(pair[1].size / 1024).toFixed(2)}KB]`
                : pair[1];
            console.log(`  ${key}: ${value}`);
        }
        
        return formData;
    };

    // ✅ UPLOAD DE ARQUIVO - Versão simplificada e robusta
    const uploadArquivo = useMutation({
        mutationFn: async (formDataOrObject) => {
            console.log('⬆️ [useMutateArquivos] Iniciando upload...');
            
            const formData = prepareFormData(formDataOrObject);
            const referenciaId = formData.get('referenciaId');
            const model = formData.get('model');
            const categoria = formData.get('categoria');
            
            if (!referenciaId || !model) {
                throw new Error('Parâmetros obrigatórios (referenciaId, model) não fornecidos');
            }
            
            // ✅ USAR APENAS A ROTA /arquivos (conforme controller)
            console.log('🔄 [useMutateArquivos] Enviando para /arquivos');
            const response = await apiClient.post('/arquivos', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                    'Accept': 'application/json'
                },
                timeout: 120000 // 2 minutos para uploads
            });
            
            console.log('✅ [useMutateArquivos] Upload bem-sucedido');
            return { 
                ...response.data, 
                params: { referenciaId, model, categoria } 
            };
        },
        onSuccess: (data, variables) => {
            console.log('✅ [useMutateArquivos] Upload concluído com sucesso:', {
                success: data.success,
                message: data.message,
                arquivoId: data.data?._id
            });
            
            // Extrair parâmetros do arquivo enviado
            let params = data.params;
            
            if (!params) {
                // Tentar extrair dos variables
                if (variables instanceof FormData) {
                    params = {
                        referenciaId: variables.get('referenciaId'),
                        model: variables.get('model'),
                        categoria: variables.get('categoria')
                    };
                } else if (variables.file && variables.referenciaId) {
                    params = {
                        referenciaId: variables.referenciaId,
                        model: variables.model,
                        categoria: variables.categoria
                    };
                } else if (data.data) {
                    params = {
                        referenciaId: data.data.referenciaId,
                        model: data.data.model,
                        categoria: data.data.categoria
                    };
                }
            }
            
            if (params && params.referenciaId && params.model) {
                // ✅ Atualizar cache otimisticamente
                const queryKey = [ARQUIVOS_QUERY_KEY, params];
                
                queryClient.setQueryData(queryKey, (oldData = []) => {
                    console.log('📝 [useMutateArquivos] Atualizando cache com novo arquivo');
                    const novoArquivo = {
                        ...data.data,
                        url: data.data?.url || data.data?.arquivo,
                        _id: data.data?._id || `temp-${Date.now()}`
                    };
                    return [novoArquivo, ...oldData];
                });
                
                // ✅ Invalidar cache após 500ms para garantir dados frescos
                setTimeout(() => {
                    invalidateCache(params);
                }, 500);
            }
            
            // ✅ Mostrar alerta de sucesso
            setAlert(data.message || 'Arquivo enviado com sucesso!', 'success', 5000);
        },
        onError: (error) => {
            console.error('❌ [useMutateArquivos] Erro no callback do upload:', {
                message: error.message,
                status: error.response?.status,
                data: error.response?.data
            });
            
            let errorMessage = 'Erro ao enviar arquivo.';
            
            if (error.response?.data?.message) {
                errorMessage = error.response.data.message;
            } else if (error.response?.data?.error) {
                const errorText = error.response.data.error;
                
                if (errorText.includes('Não autorizado') || errorText.includes('permissão')) {
                    errorMessage = 'Você não tem permissão para enviar arquivos.';
                } else if (errorText.includes('tamanho máximo') || errorText.includes('grande')) {
                    errorMessage = 'Arquivo muito grande. Tamanho máximo: 10MB';
                } else if (errorText.includes('formato') || errorText.includes('tipo')) {
                    errorMessage = 'Formato de arquivo não suportado. Use apenas imagens (JPG, PNG, GIF)';
                } else if (errorText.includes('campos obrigatórios')) {
                    errorMessage = 'Campos obrigatórios faltando.';
                } else {
                    errorMessage = errorText;
                }
            } else if (error.code === 'ECONNABORTED') {
                errorMessage = 'Tempo limite excedido. Tente um arquivo menor.';
            } else if (error.code === 'NETWORK_ERROR') {
                errorMessage = 'Erro de rede. Verifique sua conexão com a internet.';
            }
            
            setAlert(errorMessage, 'danger', 7000);
        }
    });

    // ✅ REMOÇÃO DE ARQUIVO - Versão robusta
    const removeArquivo = useMutation({
        mutationFn: async ({ arquivoId, params }) => {
            console.log('🗑️ [useMutateArquivos] Removendo arquivo:', { arquivoId, params });
            
            if (!arquivoId) {
                throw new Error('ID do arquivo não fornecido');
            }
            
            // ✅ Confirmação do usuário
            const confirmDelete = window.confirm('Tem certeza que deseja remover este arquivo permanentemente?');
            if (!confirmDelete) {
                throw new Error('Ação cancelada pelo usuário');
            }
            
            // ✅ USAR ROTA /arquivos/:id (conforme controller)
            const response = await apiClient.delete(`/arquivos/${arquivoId}`);
            
            console.log('✅ [useMutateArquivos] Remoção concluída:', response.data);
            return { 
                response: response.data, 
                params,
                arquivoId 
            };
        },
        onSuccess: (data) => {
            console.log('✅ [useMutateArquivos] Arquivo removido com sucesso');
            
            if (data.params) {
                // ✅ Remover do cache otimisticamente
                const queryKey = [ARQUIVOS_QUERY_KEY, data.params];
                
                queryClient.setQueryData(queryKey, (oldData = []) => {
                    return oldData.filter(arquivo => arquivo._id !== data.arquivoId);
                });
                
                // ✅ Invalidar cache para garantir sincronização
                setTimeout(() => {
                    invalidateCache(data.params);
                }, 300);
            }
            
            setAlert(data.response?.message || 'Arquivo removido com sucesso!', 'success', 4000);
        },
        onError: (error) => {
            if (error.message === 'Ação cancelada pelo usuário') {
                console.log('ℹ️ [useMutateArquivos] Remoção cancelada pelo usuário');
                return;
            }
            
            console.error('❌ [useMutateArquivos] Erro ao remover arquivo:', {
                message: error.message,
                status: error.response?.status,
                data: error.response?.data
            });
            
            let errorMessage = 'Erro ao remover arquivo';
            
            if (error.response?.data?.message) {
                errorMessage = error.response.data.message;
            } else if (error.response?.status === 403) {
                errorMessage = 'Você não tem permissão para remover este arquivo';
            } else if (error.response?.status === 404) {
                errorMessage = 'Arquivo não encontrado';
            }
            
            setAlert(errorMessage, 'danger', 5000);
        }
    });

    // ✅ Funções auxiliares simplificadas
    const uploadFile = (fileData) => {
        const formData = prepareFormData(fileData);
        return uploadArquivo.mutateAsync(formData);
    };

    const isPending = uploadArquivo.isPending || removeArquivo.isPending;

    return {
        // Upload
        uploadArquivo: {
            mutate: uploadArquivo.mutate,
            mutateAsync: uploadArquivo.mutateAsync,
            uploadFile,
            isLoading: uploadArquivo.isPending,
            isSuccess: uploadArquivo.isSuccess,
            isError: uploadArquivo.isError,
            error: uploadArquivo.error,
            reset: uploadArquivo.reset
        },
        
        // Delete
        removeArquivo: {
            mutate: removeArquivo.mutate,
            mutateAsync: removeArquivo.mutateAsync,
            isLoading: removeArquivo.isPending,
            isSuccess: removeArquivo.isSuccess,
            isError: removeArquivo.isError,
            error: removeArquivo.error,
            reset: removeArquivo.reset
        },
        
        // Status geral
        isPending,
        invalidateCache
    };
};

/**
 * ✅ Hook para estatísticas de arquivos - VERSÃO CORRIGIDA
 */
export const useArquivosStats = (params = {}) => {
    const { model, referenciaId, categoria } = params;
    
    return useQuery({
        queryKey: ['arquivos-stats', params],
        queryFn: async () => {
            console.log('📊 [useArquivosStats] Buscando estatísticas');
            
            try {
                const response = await apiClient.get('/arquivos/stats', {
                    params: { 
                        ...(model && { model: model.toUpperCase() }),
                        ...(referenciaId && { referenciaId }),
                        ...(categoria && { categoria: categoria.toUpperCase() })
                    }
                });
                
                if (response.data?.success) {
                    return response.data.data || {};
                }
                
                return {};
            } catch (error) {
                console.error('❌ [useArquivosStats] Erro:', error.message);
                return {};
            }
        },
        staleTime: 1000 * 60 * 5, // 5 minutos
    });
};

/**
 * ✅ Hook para testar API de upload
 */
export const useTestUploadAPI = () => {
    return useQuery({
        queryKey: ['test-upload-api'],
        queryFn: async () => {
            console.log('🧪 [useTestUploadAPI] Testando conectividade com API de arquivos');
            
            const tests = [];
            
            // Testar conexão básica
            try {
                const health = await apiClient.get('/arquivos/health');
                tests.push({ 
                    name: 'Arquivos Health Check', 
                    status: '✅', 
                    data: health.data 
                });
            } catch (error) {
                tests.push({ 
                    name: 'Arquivos Health Check', 
                    status: '❌', 
                    error: error.message 
                });
            }
            
            // Testar autenticação
            try {
                const me = await apiClient.get('/auth/me');
                tests.push({ 
                    name: 'Autenticação', 
                    status: '✅', 
                    user: me.data?.data?.name 
                });
            } catch (error) {
                tests.push({ 
                    name: 'Autenticação', 
                    status: '❌', 
                    error: error.message 
                });
            }
            
            console.log('🧪 [useTestUploadAPI] Resultados dos testes:', tests);
            return tests;
        },
        staleTime: 1000 * 60 * 15, // 15 minutos
        retry: false
    });
};

/**
 * ✅ Hook para testar busca de arquivos específica
 */
export const useTestArquivosFetch = (params) => {
    return useQuery({
        queryKey: ['test-arquivos-fetch', params],
        queryFn: async () => {
            console.log('🧪 [useTestArquivosFetch] Testando busca de arquivos:', params);
            
            if (!params?.referenciaId || !params?.model) {
                return { error: 'Parâmetros faltando' };
            }
            
            try {
                const response = await apiClient.get('/arquivos', {
                    params: { 
                        referenciaId: params.referenciaId,
                        model: params.model.toUpperCase(),
                        ...(params.categoria && { categoria: params.categoria.toUpperCase() })
                    }
                });
                
                return {
                    success: true,
                    status: response.status,
                    data: response.data,
                    arquivosCount: Array.isArray(response.data?.data) ? response.data.data.length : 0
                };
            } catch (error) {
                return {
                    success: false,
                    error: error.message,
                    status: error.response?.status,
                    data: error.response?.data
                };
            }
        },
        enabled: !!params?.referenciaId && !!params?.model,
        retry: false
    });
};

// ✅ Export padrão para compatibilidade
export default {
    useArquivos,
    useMutateArquivos,
    useArquivosStats,
    useTestUploadAPI,
    useTestArquivosFetch
};